import React, { Component } from "react";
import * as s from "./SearchResults.module.scss";
import { inject, observer } from "mobx-react";
import { toJS } from "mobx";
import { FilterStore, SearchStore, NavigationStore } from "src/stores";
import {
  Paginator,
  P,
  SingleSelectDropdown,
  LoadingRowItem
} from "src/views/shared/components";
import { tx } from "src/libs/i18n";
import { DocumentRowItem } from "../DocumentRowItem";

interface Props {
  searchStore?: SearchStore;
  filterStore?: FilterStore;
  navigationStore?: NavigationStore;
}

@inject("searchStore", "filterStore", "navigationStore")
@observer
export class SearchResults extends Component<Props> {
  renderDocumentItem = (documentObject, key) => {
    return (
      <DocumentRowItem
        key={key}
        document={toJS(documentObject)}
        loading={this.props.searchStore.isSearching}
      />
    );
  };

  setPage = number => {
    const { searchStore } = this.props;
    const { setPage } = searchStore;
    setPage(number);
  };

  renderDisplayInformation = () => {
    const { searchStore } = this.props;
    const { chunkedDocumentResults, currentPage, numberOfDocumentResults } = searchStore;

    let numberOfDocumentsOnPage = 0;
    const chunkedDocumentResultsArray = toJS(chunkedDocumentResults);
    let isResultsChunked =
      chunkedDocumentResultsArray &&
      chunkedDocumentResultsArray.some(element => Array.isArray(element));

    if (isResultsChunked) {
      numberOfDocumentsOnPage =
        chunkedDocumentResultsArray &&
        chunkedDocumentResultsArray[currentPage - 1] &&
        chunkedDocumentResultsArray[currentPage - 1].length;
    } else {
      numberOfDocumentsOnPage = chunkedDocumentResultsArray.length;
    }

    return (
      <div className={s.resultsIndicator}>
        <P className={s.resultsIndicatorText}>
          {tx("search.displayingResults", {
            displaying: numberOfDocumentsOnPage,
            totalAmount: numberOfDocumentResults
          })}
        </P>
      </div>
    );
  };

  renderPageInformation = () => {
    const { searchStore } = this.props;
    const { currentPage, numberOfPages } = searchStore;

    return (
      <div className={s.pageIndicator}>
        <P small className={s.pageIndicatorText}>
          {tx("paginator.pageOf", { currentPage, numberOfPages })}
        </P>
      </div>
    );
  };

  renderEmptyResult = () => {
    const { filterStore, navigationStore } = this.props;
    const { suggestTopics } = filterStore;
    const { location } = navigationStore;
    const suggestedTopics = suggestTopics
      ?.sort((a, b) => b.numberOfDocuments - a.numberOfDocuments)
      ?.slice(0, 5);
    return (
      <div>
        <h5 className={s.noResultsFoundTitle}>{tx("search.noResultsFound")}</h5>
        <h6 className={s.noResultsFoundTitle}>{tx("search.suggestedTerms")}</h6>
        <ul className={s.suggestTopics}>
          {suggestedTopics &&
            suggestedTopics.map(topic => (
              <li>
                <a href={`${location?.origin}/iycf_topic/${topic.slug}`}>{topic.value}</a>
              </li>
            ))}
        </ul>
      </div>
    );
  };

  renderDocumentsOrPlaceholder = () => {
    const { chunkedDocumentResults, currentPage, isSearching } = this.props?.searchStore;

    let resultIsPaginated = chunkedDocumentResults?.some(element =>
      Array.isArray(element)
    );
    let documentsToRender = resultIsPaginated
      ? chunkedDocumentResults[currentPage - 1]
      : chunkedDocumentResults;
    if (isSearching) {
      return [1, 2, 3, 4, 5, 6].map(i => <LoadingRowItem key={i} />);
    }
    if (documentsToRender?.length > 0 || isSearching) {
      return documentsToRender?.map(this.renderDocumentItem);
    } else {
      return this.renderEmptyResult();
    }
  };

  render() {
    const { searchStore, filterStore } = this.props;
    const { currentPage, numberOfPages, isSearching } = searchStore;
    const { sortResultsOptions, setSortOption } = filterStore;
    return (
      <div className={s.searchResults}>
        <div className={s.resultInformationRow}>
          {!isSearching && this.renderPageInformation()}
          {!isSearching && this.renderDisplayInformation()}
          {!isSearching && (
            <P small className={s.sortResultsLabel}>
              {`${tx("filter.sortBy")}: `}
            </P>
          )}
          {!isSearching && (
            <div className={s.sortResultsDropdownContainer}>
              <SingleSelectDropdown
                toggleOption={opt => setSortOption(opt, true)}
                options={sortResultsOptions}
              />
            </div>
          )}
        </div>
        {this.renderDocumentsOrPlaceholder()}
        {numberOfPages > 1 && (
          <Paginator
            className={s.searchResultsPagination}
            numberOfPages={numberOfPages}
            currentPage={currentPage}
            setPage={this.setPage}
          />
        )}
      </div>
    );
  }
}
